import React from 'react';
import { Box, Typography } from '@mui/material';

export interface FunnelStep {
  icon?: React.ReactNode;
  label: string;
  value: number;
  color?: string;
}

interface ConversionFunnelProps {
  steps: FunnelStep[];
  title?: string;
}

const ConversionFunnel: React.FC<ConversionFunnelProps> = ({ steps, title }) => {
  const maxValue = Math.max(...steps.map(s => s.value), 1);

  return (
    <Box sx={{ mb: 0, width: '100%' }}>
      {title && (
        <Typography
          sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '14px',
            fontWeight: 700,
            color: 'text.primary',
            mb: 2,
          }}
        >
          {title}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: { xs: 0.5, sm: 1 }, height: 200, width: '100%' }}>
        {steps.map((step, index) => {
          const pct = maxValue > 0 ? (step.value / maxValue) * 100 : 0;
          const convPct = index > 0 && steps[0].value > 0
            ? ((step.value / steps[0].value) * 100).toFixed(1)
            : '100.0';
          const barColor = step.color || `hsl(${180 - (index * 25)}, 60%, ${40 + index * 5}%)`;

          return (
            <Box
              key={step.label}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                height: '100%',
                justifyContent: 'flex-end',
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '18px',
                  fontWeight: 800,
                  color: 'text.primary',
                  mb: 0.5,
                }}
              >
                {step.value.toLocaleString()}
              </Typography>
              <Typography
                sx={{
                  fontSize: '10px',
                  color: 'text.secondary',
                  mb: 0.5,
                }}
              >
                {convPct}%
              </Typography>
              <Box
                sx={{
                  width: 'min(92%, 200px)',
                  height: `${Math.max(pct, 8)}%`,
                  bgcolor: barColor,
                  borderRadius: '8px 8px 4px 4px',
                  transition: 'height 0.5s ease',
                  position: 'relative',
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                {step.icon && (
                  <Box sx={{ color: barColor, display: 'flex', fontSize: 14 }}>{step.icon}</Box>
                )}
                <Typography
                  sx={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: '10px',
                    fontWeight: 600,
                    color: 'text.secondary',
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                  }}
                >
                  {step.label}
                </Typography>
              </Box>
              {index < steps.length - 1 && (
                <Box
                  sx={{
                    position: 'absolute',
                    display: 'none', // arrows handled by gap
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ConversionFunnel;
